import { useRef, useState } from "preact/hooks";
import type { Action } from "@cos/shared";
import type { Store } from "../state.js";
import { useVisualViewport } from "../useVisualViewport.js";
import { useWidgetState } from "../useWidgetState.js";
import { Launcher } from "./Launcher.js";
import { MessageList } from "./MessageList.js";
import { Composer } from "./Composer.js";

export type ChatMode = "window" | "fullscreen" | "inline";
export type LauncherPosition = "bottom-right" | "bottom-left";

export interface AppProps {
  store: Store;
  mode: ChatMode;
  position: LauncherPosition;
  /** `"hidden"`: the host page draws its own launcher; see `CreateChatOptions.launcher`. */
  launcher?: "default" | "hidden";
  onSend: (text: string, files: File[]) => void;
  /** Owned by the caller so opening the panel can clear the unread badge in the same update. */
  onToggle: () => void;
  onQuickReply: (value: string) => void;
  onAction: (action: Action) => void;
  onOrderConfirm: (orderId: string) => void;
  onFormSubmit: (fields: Record<string, string>) => void;
  onFeedback: (messageId: string, rating: number, comment?: string) => void;
  /** Ends this conversation and starts a fresh one — the kebab menu's single action. */
  onNewConversation: () => void;
  onPushAccept: () => void;
  onPushDismiss: () => void;
}

export function App(props: AppProps) {
  const state = useWidgetState(props.store);
  const open = props.mode === "inline" || state.panelOpen;
  // Held here rather than in the message list so the overlay covers the
  // whole panel instead of being clipped by the scrolling transcript.
  const [zoomed, setZoomed] = useState<string | undefined>();

  // Inline mode sits in the host page's own flow, so the host decides how
  // tall it is and what an open keyboard does to it — measuring there would
  // be us moving someone else's furniture.
  const rootRef = useRef<HTMLDivElement>(null);
  useVisualViewport(rootRef, props.mode !== "inline");

  return (
    <div class="cos-root" ref={rootRef} data-mode={props.mode} data-position={props.position}>
      {props.mode !== "inline" && props.launcher !== "hidden" && (
        <Launcher
          open={state.panelOpen}
          botName={state.botName}
          unreadCount={state.unreadCount}
          teaser={state.teaser}
          agentOnline={state.control === "human"}
          agentTyping={state.agentTyping}
          onToggle={props.onToggle}
        />
      )}
      {open && (
        <div class="cos-panel" role="dialog" aria-label={`Chat with ${state.botName || "us"}`}>
          <header class="cos-header">
            {/* Never a name: the shopper learns a person is replying, not who
                (the server stopped sending one — see `statusChangeSchema`). */}
            <span class="cos-bot-name">{state.control === "human" ? "Talking to a person" : state.botName}</span>
            {props.mode !== "inline" && (
              <button type="button" class="cos-close" aria-label="Close chat" onClick={() => props.store.set({ panelOpen: false })}>
                ×
              </button>
            )}
          </header>
          <MessageList
            messages={state.messages}
            greeting={state.greeting}
            botTyping={state.botTyping || state.agentTyping}
            onQuickReply={props.onQuickReply}
            onAction={props.onAction}
            onOrderConfirm={props.onOrderConfirm}
            onFormSubmit={props.onFormSubmit}
            onFeedback={props.onFeedback}
            onOpenImage={setZoomed}
          />
          {state.pushPrompt && (
            <div class="cos-push" role="group" aria-label="Notifications">
              <p>Get a notification when we reply — even if you leave this page.</p>
              <div>
                <button type="button" class="cos-push-yes" disabled={state.pushPrompt === "busy"} onClick={props.onPushAccept}>
                  Notify me
                </button>
                <button type="button" class="cos-push-no" onClick={props.onPushDismiss}>
                  No thanks
                </button>
              </div>
            </div>
          )}
          {/* The panel only exists while it's open, so mounting the composer
              *is* the open — see `autoFocus` in Composer.tsx. Inline mode is
              the exception: it mounts with the page, and a widget that grabs
              the caret on load would take it from whatever the shopper came
              to the page to do. */}
          <Composer
            onSend={props.onSend}
            allowImages={state.allowImageUploads}
            autoFocus={props.mode !== "inline"}
            onNewConversation={props.onNewConversation}
          />
          {zoomed && (
            // Click anywhere to dismiss: a lightbox with only a small × is a
            // trap on a phone, which is where most of these are opened.
            <div class="cos-lightbox" role="dialog" aria-label="Image" onClick={() => setZoomed(undefined)}>
              <img src={zoomed} alt="Attached image" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
